import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { ensureDLToken } from "@/lib/dlToken";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Camera,
  CameraOff,
  ScanLine,
  CheckCircle2,
  AlertTriangle,
  Package,
  Route as RouteIcon,
  Truck,
  PackagePlus,
  Keyboard,
  FileSpreadsheet,
  Printer,
  Trash2,
  X,
  Loader2,
} from "lucide-react";

const STATUS_LABEL = {
  pending: "Pending",
  assigned: "Assigned",
  in_transit: "In transit",
  delivered: "Delivered",
  failed: "Failed",
  returned: "Returned",
};

/**
 * /scan — three sub-tabs:
 *   1) Accept   → POST /orders/intake (marks order received from pharmacy)
 *   2) Inventory → GET /orders/lookup (read-only — show route + driver)
 *   3) Customer  → Navigate to /admin/orders/new?prefill=<code>&autoprint=1
 */
export default function ScanPackage() {
  const { user } = useAuth();
  const role = user?.role;
  const isDriver = role === "driver";
  const isPharmacyStaff = role === "pharmacy_staff";
  // Pharmacy staff land directly on Customer Scan — they don't see the other tabs.
  const newOrderHref = role === "dispatcher" ? "/dispatch/orders/new" : (isPharmacyStaff ? "/staff/new" : "/admin/orders/new");
  const [mode, setMode] = useState(isPharmacyStaff ? "customer" : "accept");

  // Reset scroll to the top of the page on mount and whenever the sub-tab
  // changes — landing at the top of the header (not midway down on a tall
  // camera card) is what the user expects.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [mode]);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-6" data-testid="scan-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Inventory · Scan</div>
        <h1 className="font-display text-3xl mt-1">Scan</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Use the device camera (phone) or a USB scanner gun (desktop) — the input below auto-submits when the gun sends ENTER.
        </p>
      </header>

      <Tabs value={mode} onValueChange={setMode} className="w-full">
        <TabsList className="mb-4" data-testid="scan-tabs">
          {!isPharmacyStaff && (
            <TabsTrigger value="accept" data-testid="scan-tab-accept">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Accept Scan
            </TabsTrigger>
          )}
          {!isPharmacyStaff && (
            <TabsTrigger value="inventory" data-testid="scan-tab-inventory">
              <Package className="h-3.5 w-3.5 mr-1.5" /> Inventory Scan
            </TabsTrigger>
          )}
          {!isDriver && (
            <TabsTrigger value="print" data-testid="scan-tab-print">
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Print Label
            </TabsTrigger>
          )}
          {!isDriver && (
            <TabsTrigger value="customer" data-testid="scan-tab-customer">
              <PackagePlus className="h-3.5 w-3.5 mr-1.5" /> Customer Scan
            </TabsTrigger>
          )}
        </TabsList>

        {!isPharmacyStaff && (
          <TabsContent value="accept" data-testid="scan-tab-accept-content">
            <AcceptScan />
          </TabsContent>
        )}

        {!isPharmacyStaff && (
          <TabsContent value="inventory" data-testid="scan-tab-inventory-content">
            <InventoryScan />
          </TabsContent>
        )}

        {!isDriver && (
          <TabsContent value="print" data-testid="scan-tab-print-content">
            <PrintLabelScan />
          </TabsContent>
        )}

        {!isDriver && (
          <TabsContent value="customer" data-testid="scan-tab-customer-content">
            <CustomerScan newOrderHref={newOrderHref} canUpload={!isPharmacyStaff} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ============================================================ Accept Scan

function AcceptScan() {
  const scannerRef = useRef(null);
  const regionRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const lastSentRef = useRef({ code: null, at: 0 });

  useEffect(() => () => stopScanner().catch(() => {}), []);

  async function startScanner() {
    if (running) return;
    let Html5Qrcode;
    try {
      ({ Html5Qrcode } = await import("html5-qrcode"));
    } catch {
      toast.error("Camera library failed to load");
      return;
    }
    if (!scannerRef.current) scannerRef.current = new Html5Qrcode("accept-scan-region", false);
    try {
      await scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        onDecoded,
        () => {},
      );
      setRunning(true);
    } catch (e) {
      toast.error("Couldn't access camera — check browser permissions");
      console.error(e);
    }
  }

  async function stopScanner() {
    if (!scannerRef.current) return;
    try {
      if (scannerRef.current.isScanning) await scannerRef.current.stop();
      await scannerRef.current.clear();
    } catch {}
    setRunning(false);
  }

  async function onDecoded(decodedText) {
    const now = Date.now();
    if (lastSentRef.current.code === decodedText && now - lastSentRef.current.at < 2500) return;
    lastSentRef.current = { code: decodedText, at: now };
    // Optimistic feedback — flash a pending card + short beep-style toast the
    // millisecond the camera decodes, so the pharmacy staff feels the scan
    // even while the API round-trip finishes. Real result overwrites this
    // in ~150-200 ms once /orders/intake returns.
    setLast({ ok: true, pending: true, scanned: decodedText });
    await submitIntake(decodedText);
  }

  async function submitIntake(code) {
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orders/intake", { code });
      setLast(data);
      const o = data.order || {};
      if (data.already_received) toast.warning(`Already received: ${o.order_number || o.id?.slice(0, 8)}`);
      else toast.success(`Package accepted: ${o.order_number || o.id?.slice(0, 8)}`);
    } catch (err) {
      setLast({ ok: false, error: formatApiError(err), scanned: code });
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitManual(e) {
    e.preventDefault();
    if (!manualCode.trim()) return;
    // Same optimistic flash for the USB gun / manual submit path.
    setLast({ ok: true, pending: true, scanned: manualCode.trim() });
    await submitIntake(manualCode.trim());
    setManualCode("");
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-accent/5 border-accent/20">
        <p className="text-sm">
          <CheckCircle2 className="h-4 w-4 inline -mt-0.5 mr-1 text-accent" />
          Scans here mark the package as <strong>Accepted (Received from pharmacy)</strong> on the order timeline. Idempotent — re-scanning the same package is safe.
        </p>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="relative bg-black aspect-[4/3] grid place-items-center">
          <div id="accept-scan-region" ref={regionRef} className="w-full h-full" data-testid="accept-scan-region" />
          {!running && (
            <div className="absolute inset-0 grid place-items-center text-white/85 pointer-events-none">
              <div className="text-center">
                <ScanLine className="h-10 w-10 mx-auto mb-2 opacity-70" />
                <div className="text-sm">Camera is off</div>
                <div className="text-xs text-white/55 mt-1">Tap "Start camera" or use the gun input below</div>
              </div>
            </div>
          )}
          {running && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-[260px] h-[260px] rounded-2xl border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"></div>
            </div>
          )}
        </div>
        <div className="p-3 flex items-center gap-2 justify-center border-t border-border bg-card">
          {!running ? (
            <Button onClick={startScanner} className="rounded-full px-5" data-testid="accept-scan-start-btn">
              <Camera className="h-4 w-4 mr-1.5" /> Start camera
            </Button>
          ) : (
            <Button variant="outline" onClick={stopScanner} className="rounded-full px-5" data-testid="accept-scan-stop-btn">
              <CameraOff className="h-4 w-4 mr-1.5" /> Stop camera
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-4">
        <form onSubmit={submitManual} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Keyboard className="h-3 w-3" /> Scanner gun / manual entry
            </label>
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Scan with USB gun or type · ENTER to submit"
              className="mt-1"
              autoFocus
              data-testid="accept-scan-manual-input"
            />
          </div>
          <Button type="submit" disabled={busy || !manualCode.trim()} className="rounded-full" data-testid="accept-scan-manual-submit">
            Accept package
          </Button>
        </form>
      </Card>

      {last && <AcceptResultCard last={last} />}
    </div>
  );
}

function AcceptResultCard({ last }) {
  // Pending state — shown for the ~150 ms between decode and API response.
  if (last.pending) {
    return (
      <Card className="p-4 border-l-4 border-l-primary/60 animate-pulse" data-testid="accept-scan-result-pending">
        <div className="flex items-start gap-3">
          <Loader2 className="h-5 w-5 text-primary shrink-0 mt-0.5 animate-spin" />
          <div>
            <div className="font-medium">Processing scan…</div>
            <div className="text-xs font-mono mt-1 text-muted-foreground break-all">{last.scanned}</div>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card className={`p-4 border-l-4 ${last.ok === false ? "border-l-destructive" : last.already_received ? "border-l-amber-400" : "border-l-accent"}`} data-testid="accept-scan-result">
      {last.ok === false ? (
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Couldn't process scan</div>
            <div className="text-sm text-muted-foreground">{last.error}</div>
            {last.scanned && <div className="text-xs font-mono mt-1 text-muted-foreground break-all">{last.scanned}</div>}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          {last.already_received ? (
            <Package className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              {last.already_received ? "Already accepted" : "Accepted from pharmacy"}
              <span className="ml-2 font-mono text-sm text-muted-foreground">#{last.order?.order_number || last.order?.id?.slice(0, 8)}</span>
            </div>
            <div className="text-sm mt-0.5">{last.order?.customer_name}</div>
            <div className="text-xs text-muted-foreground truncate">{last.order?.address}</div>
            <div className="text-[11px] text-muted-foreground mt-1">
              At {new Date(last.received_from_pharmacy_at).toLocaleString()}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// ============================================================ Print Label Scan
// One-scan-to-print. Scans an Order # or Primary Key (QR), matches an order
// via GET /orders/lookup, and immediately opens the 4x6 label PDF in a new
// tab (which the browser prints). No confirmation dialog, no second scan.

function PrintLabelScan() {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [manualCode, setManualCode] = useState("");

  async function handleScan(code) {
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.get("/orders/lookup", { params: { code } });
      const o = data?.order;
      if (!o?.id) throw new Error("No order id returned");
      // Immediately open the label PDF in a new tab — the browser handles the
      // rest (print preview or auto-download depending on the browser's PDF
      // handler). Uses the short-lived download token so we don't leak the
      // access JWT in the URL.
      const t = await ensureDLToken();
      const url = `${BACKEND_URL}/api/orders/${o.id}/label.pdf${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
      window.open(url, "_blank", "noopener");
      setLast({ ok: true, order: o, scanned: code, matched_by: data.matched_by });
      toast.success(`Label printed: #${o.order_number || o.id.slice(0, 8)}`);
    } catch (err) {
      setLast({ ok: false, error: formatApiError(err), scanned: code });
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitManual(e) {
    e.preventDefault();
    if (!manualCode.trim()) return;
    await handleScan(manualCode.trim());
    setManualCode("");
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-primary/5 border-primary/20">
        <p className="text-sm">
          <Printer className="h-4 w-4 inline -mt-0.5 mr-1 text-primary" />
          Scan an <strong>Order #</strong> or <strong>Primary Key (QR)</strong> with your <strong>USB scanner gun</strong> — the matching 4×6 label opens in a new tab and prints automatically. One scan is all it takes.
        </p>
      </Card>

      <Card className="p-4">
        <form onSubmit={submitManual} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Keyboard className="h-3 w-3" /> Scanner gun / manual entry
            </label>
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Scan Order # or QR · ENTER to print"
              className="mt-1"
              autoFocus
              data-testid="print-scan-manual-input"
            />
          </div>
          <Button type="submit" disabled={busy || !manualCode.trim()} className="rounded-full" data-testid="print-scan-manual-submit">
            <Printer className="h-4 w-4 mr-1.5" /> Print label
          </Button>
        </form>
      </Card>

      {last && <PrintLabelResultCard last={last} />}
    </div>
  );
}

function PrintLabelResultCard({ last }) {
  return (
    <Card className={`p-4 border-l-4 ${last.ok === false ? "border-l-destructive" : "border-l-primary"}`} data-testid="print-scan-result">
      {last.ok === false ? (
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Couldn't match this scan</div>
            <div className="text-sm text-muted-foreground">{last.error}</div>
            {last.scanned && <div className="text-xs font-mono mt-1 text-muted-foreground break-all">{last.scanned}</div>}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <Printer className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              Label printed
              <span className="ml-2 font-mono text-sm text-muted-foreground">#{last.order?.order_number || last.order?.id?.slice(0, 8)}</span>
              {last.matched_by === "primary_key" && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-secondary text-secondary-foreground text-[10px] uppercase tracking-wider">via QR</span>
              )}
            </div>
            <div className="text-sm mt-0.5">{last.order?.customer_name}</div>
            <div className="text-xs text-muted-foreground truncate">{last.order?.address}</div>
            {last.order?.primary_key && (
              <div className="text-[11px] text-muted-foreground mt-1 font-mono" data-testid="print-scan-result-pk">PK: {last.order.primary_key}</div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ============================================================ Inventory Scan

function InventoryScan() {
  const scannerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const lastSentRef = useRef({ code: null, at: 0 });

  useEffect(() => () => stopScanner().catch(() => {}), []);

  async function startScanner() {
    if (running) return;
    let Html5Qrcode;
    try {
      ({ Html5Qrcode } = await import("html5-qrcode"));
    } catch {
      toast.error("Camera library failed to load");
      return;
    }
    if (!scannerRef.current) scannerRef.current = new Html5Qrcode("inventory-scan-region", false);
    try {
      await scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        onDecoded,
        () => {},
      );
      setRunning(true);
    } catch (e) {
      toast.error("Couldn't access camera — check browser permissions");
      console.error(e);
    }
  }

  async function stopScanner() {
    if (!scannerRef.current) return;
    try {
      if (scannerRef.current.isScanning) await scannerRef.current.stop();
      await scannerRef.current.clear();
    } catch {}
    setRunning(false);
  }

  async function onDecoded(decodedText) {
    const now = Date.now();
    if (lastSentRef.current.code === decodedText && now - lastSentRef.current.at < 2500) return;
    lastSentRef.current = { code: decodedText, at: now };
    // Optimistic "processing" flash — same trick as Accept scan so the
    // operator sees the scan register instantly.
    setResult({ ok: true, pending: true, scanned: decodedText });
    await lookup(decodedText);
  }

  async function lookup(raw) {
    const c = (raw ?? code).trim();
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await api.get("/scan/lookup", { params: { code: c } });
      setResult({ ok: true, ...data });
    } catch (e) {
      setResult({ ok: false, error: formatApiError(e), scanned: c });
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-primary/5 border-primary/20">
        <p className="text-sm">
          <Package className="h-4 w-4 inline -mt-0.5 mr-1 text-primary" />
          Look up where a package is in the routing pipeline — <strong>read-only</strong>, no status change. Use the phone camera or a USB scanner gun.
        </p>
      </Card>

      {/* 1st — Scan result square (sits at the top so the user sees it
          immediately after a scan, without scrolling past the camera/gun). */}
      {result && (
        <Card className={`p-4 border-l-4 ${result.ok === false ? "border-l-destructive" : result.pending ? "border-l-primary/60 animate-pulse" : "border-l-primary"}`} data-testid="inventory-scan-result">
          {result.pending ? (
            <div className="flex items-start gap-3">
              <Loader2 className="h-5 w-5 text-primary shrink-0 mt-0.5 animate-spin" />
              <div>
                <div className="font-medium">Looking up package…</div>
                <div className="text-xs font-mono mt-1 text-muted-foreground break-all">{result.scanned}</div>
              </div>
            </div>
          ) : result.ok === false ? (
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Not found</div>
                <div className="text-sm text-muted-foreground">{result.error}</div>
                {result.scanned && <div className="text-xs font-mono mt-1 text-muted-foreground break-all">{result.scanned}</div>}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Package className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium font-mono text-sm" data-testid="inventory-order-id">
                    #{result.order?.order_number || result.order?.id?.slice(0, 8)}
                  </div>
                  <div className="text-base font-medium mt-0.5" data-testid="inventory-customer-name">{result.order?.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{result.order?.address}</div>
                  {result.order?.customer_phone && (
                    <div className="text-xs text-muted-foreground font-mono">{result.order.customer_phone}</div>
                  )}
                </div>
                <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                  result.order?.status === "delivered" ? "bg-accent/15 text-accent" :
                  result.order?.status === "failed" ? "bg-destructive/15 text-destructive" :
                  result.order?.status === "in_transit" ? "bg-[#3a86ff]/15 text-[#1f4ea3]" :
                  "bg-secondary text-secondary-foreground"
                }`} data-testid="inventory-status">
                  {STATUS_LABEL[result.order?.status] || result.order?.status}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-border">
                <div data-testid="inventory-route-block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                    <RouteIcon className="h-3 w-3" /> Route
                  </div>
                  {result.route ? (
                    <div className="mt-1">
                      <div className="text-sm font-medium" data-testid="inventory-route-name">{result.route.label || "—"}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {result.route.status?.replace("_", " ")}
                        {result.order?.sequence != null && ` · stop #${result.order.sequence}`}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1 text-sm text-muted-foreground italic">Not on any route yet</div>
                  )}
                </div>
                <div data-testid="inventory-driver-block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                    <Truck className="h-3 w-3" /> Driver
                  </div>
                  {result.driver ? (
                    <div className="mt-1">
                      <div className="text-sm font-medium" data-testid="inventory-driver-name">{result.driver.name}</div>
                      {result.driver.phone && <div className="text-[11px] text-muted-foreground font-mono">{result.driver.phone}</div>}
                    </div>
                  ) : (
                    <div className="mt-1 text-sm text-muted-foreground italic">No driver assigned</div>
                  )}
                </div>
                {result.pharmacy && (
                  <div className="sm:col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Pharmacy</div>
                    <div className="mt-1 text-sm">{result.pharmacy.nickname || result.pharmacy.name}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* 2nd — Phone-camera square */}
      <Card className="p-0 overflow-hidden">
        <div className="relative bg-black aspect-[4/3] grid place-items-center">
          <div id="inventory-scan-region" className="w-full h-full" data-testid="inventory-scan-region" />
          {!running && (
            <div className="absolute inset-0 grid place-items-center text-white/85 pointer-events-none">
              <div className="text-center">
                <ScanLine className="h-10 w-10 mx-auto mb-2 opacity-70" />
                <div className="text-sm">Camera is off</div>
                <div className="text-xs text-white/55 mt-1">Tap "Start camera" or use the gun input below</div>
              </div>
            </div>
          )}
          {running && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-[260px] h-[260px] rounded-2xl border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"></div>
            </div>
          )}
        </div>
        <div className="p-3 flex items-center gap-2 justify-center border-t border-border bg-card">
          {!running ? (
            <Button onClick={startScanner} className="rounded-full px-5" data-testid="inventory-scan-start-btn">
              <Camera className="h-4 w-4 mr-1.5" /> Start camera
            </Button>
          ) : (
            <Button variant="outline" onClick={stopScanner} className="rounded-full px-5" data-testid="inventory-scan-stop-btn">
              <CameraOff className="h-4 w-4 mr-1.5" /> Stop camera
            </Button>
          )}
        </div>
      </Card>

      {/* 3rd — Scanner-gun input */}
      <Card className="p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            lookup();
            setCode("");
          }}
          className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end"
        >
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Keyboard className="h-3 w-3" /> Scanner gun / order number
            </label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Scan with USB gun · ENTER to look up"
              className="mt-1"
              autoFocus
              data-testid="inventory-scan-input"
            />
          </div>
          <Button type="submit" disabled={busy || !code.trim()} className="rounded-full" data-testid="inventory-scan-submit">
            Look up
          </Button>
        </form>
      </Card>
    </div>
  );
}

// ============================================================ Customer Scan

function CustomerScan({ newOrderHref, canUpload = true }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [rosterStatus, setRosterStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [duplicate, setDuplicate] = useState(null); // existing order info on 409
  // 3-scan state machine: idle → loaded (1st scan pulled info) → created
  // (2nd scan saved order) → done (3rd scan printed label). The dispatcher
  // just keeps the gun pointed at the same package — no clicks at all.
  const [phase, setPhase] = useState("idle"); // "idle" | "loaded" | "created" | "done"
  const [pulled, setPulled] = useState(null); // roster row with patient + defaults
  const [createdOrder, setCreatedOrder] = useState(null); // backend order on save

  // Column-mapping flow state — once a file is picked we inspect headers and
  // show the user a small dialog to map their columns to our standard fields.
  const [pendingFile, setPendingFile] = useState(null);
  const [inspectData, setInspectData] = useState(null); // { columns, samples, suggested_mapping, saved_mapping }
  const [mapping, setMapping] = useState(null); // { order_number: [col], customer_name: [col, col], ... }
  const [sheets, setSheets] = useState([]); // uploaded sheets list (right panel)
  // Per-batch defaults set in the mapping dialog and applied to every order
  // created from rows of this upload.
  const [pharmacies, setPharmacies] = useState([]);
  const [defaultPharmacyId, setDefaultPharmacyId] = useState("");
  const [defaultDeliverType, setDefaultDeliverType] = useState("same_day");
  const [defaultPriority, setDefaultPriority] = useState("normal");

  // Auto-submit timer — handles guns that DON'T send Enter (rare, but safer)
  const autoSubmitRef = useRef(null);

  useEffect(() => {
    loadStatus();
    loadSheets();
    // Pre-load pharmacies for the per-batch defaults dropdown
    api.get("/pharmacies").then((r) => setPharmacies(r.data || [])).catch(() => {});
    // Auto-focus the input so the gun can fire immediately
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/scan/roster/status");
      setRosterStatus(data);
    } catch {
      setRosterStatus({ total: 0, remaining: 0 });
    }
  };

  const loadSheets = async () => {
    try {
      const { data } = await api.get("/scan/roster/sheets");
      setSheets(Array.isArray(data) ? data : []);
    } catch {
      setSheets([]);
    }
  };

  const deleteSheet = async (sheet) => {
    if (!window.confirm(`Delete "${sheet.filename}" and all ${sheet.rows_remaining + sheet.rows_consumed} of its rows?`)) return;
    try {
      await api.delete(`/scan/roster/sheets/${sheet.id}`);
      toast.success(`Deleted ${sheet.filename}`);
      await loadStatus();
      await loadSheets();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onInputChange = (val) => {
    setCode(val);
    setLookupError(null);
    setDuplicate(null);
    if (autoSubmitRef.current) clearTimeout(autoSubmitRef.current);
    if (val.trim().length >= 3) {
      autoSubmitRef.current = setTimeout(() => doLookup(val.trim()), 650);
    }
  };

  const submit = (e) => {
    e?.preventDefault?.();
    if (autoSubmitRef.current) clearTimeout(autoSubmitRef.current);
    const c = code.trim();
    if (!c) return;
    doLookup(c);
  };

  const resetFlow = () => {
    setPhase("idle");
    setPulled(null);
    setCreatedOrder(null);
    setCode("");
    setLookupError(null);
    setDuplicate(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const doLookup = async (c) => {
    if (busy) return;
    setBusy(true);

    // 2nd scan — same code, in "loaded" phase: SAVE the order.
    // For Magnum Compounding the operator keeps scanning the QR PRIMARY KEY,
    // so we accept a match on ANY of: order_number, primary_key, or the
    // raw scanned_code the backend echoed back on the 1st scan (this last
    // one is critical when Excel mangled the primary_key with float
    // precision loss — the scanner output won't equal the stored value).
    if (
      phase === "loaded" && pulled && (
        c.toUpperCase() === pulled.order_number ||
        (pulled.primary_key && c.toUpperCase() === pulled.primary_key) ||
        (pulled.scanned_code && c.toUpperCase() === pulled.scanned_code)
      )
    ) {
      try {
        const payload = {
          customer_name: pulled.customer_name || "",
          customer_phone: pulled.customer_phone || "",
          address: pulled.address || "",
          pharmacy_id: pulled.default_pharmacy_id,
          priority: pulled.default_priority || "normal",
          deliver_type: pulled.default_deliver_type || "same_day",
          items: [{ name: "Pharmacy Delivery", qty: 1 }],
          notes: pulled.delivery_notes || "",
          order_number: pulled.order_number,
        };
        const { data } = await api.post("/orders", payload);
        await api.post(`/scan/roster/${encodeURIComponent(pulled.order_number)}/consume`).catch(() => {});
        setCreatedOrder(data);
        setPhase("created");
        toast.success(`Order #${data.order_number || data.id?.slice(0, 8)} saved — scan again to print`);
        loadStatus();
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setBusy(false);
        setCode("");
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      return;
    }

    // 3rd scan — same code, in "created" phase: PRINT the label.
    // Same triple-match logic as the save phase so all three scans work
    // consistently with a mangled Magnum QR.
    if (
      phase === "created" && createdOrder && (
        c.toUpperCase() === (createdOrder.order_number || "").toUpperCase() ||
        (pulled?.primary_key && c.toUpperCase() === pulled.primary_key) ||
        (pulled?.scanned_code && c.toUpperCase() === pulled.scanned_code)
      )
    ) {
      // SEC-003: mint a short-lived download token instead of leaking the
      // long access JWT into browser history.
      const token = await ensureDLToken().catch(() => "");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${createdOrder.id}/label.pdf${token ? `?auth=${encodeURIComponent(token)}` : ""}`;

      // Open a controlled popup that loads the PDF in an iframe, fires the
      // browser print dialog, and auto-closes itself when the user finishes
      // (or cancels) the print — so the dispatcher never has to alt-tab and
      // close a leftover tab between packages.
      const popup = window.open("", "PharmaRouteLabel", "width=520,height=720,menubar=no,toolbar=no");
      if (!popup) {
        // Popup blocked — fall back to a plain tab so they still get the label.
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success("Label sent to printer tab");
      } else {
        const html = `<!doctype html><html><head><title>Print label</title>
<style>html,body{margin:0;height:100%;background:#0f0f10;color:#e7e7ea;font-family:system-ui,sans-serif}
.bar{position:fixed;top:0;left:0;right:0;display:flex;align-items:center;justify-content:space-between;padding:8px 12px;background:#1a1a1d;border-bottom:1px solid #2a2a2e;font-size:12px;z-index:2}
.bar button{background:#dc2626;color:#fff;border:0;padding:6px 14px;border-radius:6px;font-weight:600;cursor:pointer}
iframe{border:0;position:absolute;top:42px;left:0;right:0;bottom:0;width:100%;height:calc(100% - 42px);background:#fff}</style>
</head><body>
<div class="bar"><span>Label · Order ${String(createdOrder.order_number || "").replace(/[<>"&]/g, "")}</span><button id="x" type="button">Close</button></div>
<iframe id="frame" src="${url}"></iframe>
<script>
(function(){
  var f=document.getElementById('frame');
  var closed=false;
  function done(){ if(closed) return; closed=true; try{ window.close(); }catch(e){} }
  document.getElementById('x').addEventListener('click', done);
  window.addEventListener('afterprint', function(){ setTimeout(done, 400); });
  f.addEventListener('load', function(){
    try{
      f.contentWindow.focus();
      f.contentWindow.print();
    }catch(e){
      // Cross-origin or PDF-viewer block — leave popup open so user can print manually.
      console.warn('Auto-print failed', e);
    }
  });
  // Safety net: if afterprint never fires (some browsers swallow it for PDFs),
  // auto-close after 30s so we don't litter desktops with leftover popups.
  setTimeout(done, 30000);
})();
<\/script>
</body></html>`;
        popup.document.open();
        popup.document.write(html);
        popup.document.close();
        toast.success("Label sent to printer");
      }
      setPhase("done");
      setBusy(false);
      // Auto-reset for the next package after a brief moment
      setTimeout(resetFlow, 1200);
      return;
    }

    // 1st scan (or new code mid-flow) — PULL roster info
    setLookupError(null);
    setDuplicate(null);
    setPulled(null);
    setCreatedOrder(null);
    try {
      const { data } = await api.get("/scan/roster/lookup", { params: { code: c } });
      setPulled(data);
      setPhase("loaded");
      if (data.matched_via_primary_key) {
        // Give the operator visible confirmation that the QR PRIMARY KEY
        // pivoted to the actual Order # — matches the Magnum workflow.
        toast.success(`QR ${data.scanned_code} matched Order #${data.order_number} — ${data.customer_name || ""}`.trim());
      } else {
        toast.success(`Pulled ${data.customer_name || c} — scan again to save`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && detail.code === "DUPLICATE_SCAN") {
        setDuplicate(detail);
        toast.error(detail.message || "Duplicate scan");
      } else {
        setLookupError(formatApiError(e));
      }
      setPhase("idle");
    } finally {
      setBusy(false);
      setCode("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setInspectData(null);
    try {
      // Step 1 — inspect headers + sample rows so we can show a mapping UI
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/scan/roster/inspect", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPendingFile(f);
      setInspectData(data);
      // Seed mapping: prefer last-saved, but only keep columns that actually
      // exist in THIS file's header row — otherwise we'd silently upload rows
      // with empty order_number when the user re-uses a saved mapping from
      // a previous file that had different headers.
      const availableCols = new Set((data.columns || []).map((c) => String(c)));
      const filterToAvailable = (cols) =>
        (cols || []).filter((c) => availableCols.has(String(c)));
      const saved = data.saved_mapping || {};
      const suggested = data.suggested_mapping || {};
      const pickField = (k) => {
        const savedCols = filterToAvailable(saved[k]);
        if (savedCols.length > 0) return savedCols;
        return filterToAvailable(suggested[k]);
      };
      setMapping({
        primary_key: pickField("primary_key"),
        order_number: pickField("order_number"),
        customer_name: pickField("customer_name"),
        customer_phone: pickField("customer_phone"),
        address: pickField("address"),
        delivery_notes: pickField("delivery_notes"),
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmMappingAndUpload = async () => {
    if (!pendingFile || !mapping) return;
    if (!mapping.order_number || mapping.order_number.length === 0) {
      toast.error("Please map a column to Order #");
      return;
    }
    if (!defaultPharmacyId) {
      toast.error("Please pick a default Pharmacy for this batch");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("default_pharmacy_id", defaultPharmacyId);
      fd.append("default_deliver_type", defaultDeliverType);
      fd.append("default_priority", defaultPriority);
      const { data } = await api.post("/scan/roster/upload?replace=false", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.inserted) {
        toast.error(`Roster upload inserted 0 rows${data.skipped ? ` (${data.skipped} skipped — duplicate or blank Order #)` : " — check that the Order # column was mapped correctly"}`);
      } else {
        toast.success(`Roster uploaded — ${data.inserted} row${data.inserted === 1 ? "" : "s"}${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
      }
      setPendingFile(null);
      setInspectData(null);
      setMapping(null);
      await loadStatus();
      await loadSheets();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const cancelMapping = () => {
    setPendingFile(null);
    setInspectData(null);
    setMapping(null);
  };

  const clearRoster = async () => {
    if (!window.confirm("Clear the entire scan roster?")) return;
    try {
      await api.post("/scan/roster/clear");
      toast.success("Roster cleared");
      await loadStatus();
      await loadSheets();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const goManual = () => {
    const c = code.trim();
    try { sessionStorage.removeItem("scanRosterPrefill"); } catch {}
    navigate(`${newOrderHref}${c ? `?prefill=${encodeURIComponent(c)}&autoprint=1` : ""}`);
  };

  return (
    <div className={`grid grid-cols-1 ${canUpload ? "lg:grid-cols-[1fr_320px]" : ""} gap-4 items-start`}>
      <div className="space-y-4">
      <Card className="p-4 bg-amber-50 dark:bg-amber-950/30 border-amber-300/50">
        <p className="text-sm">
          <PackagePlus className="h-4 w-4 inline -mt-0.5 mr-1 text-amber-600" />
          {canUpload ? (
            <>Upload a roster (xlsx / csv) mapping <strong>Order #</strong> → patient details, then scan an Order #. Patient Name, Phone, Address, and Notes are auto-filled. You just pick <strong>Pharmacy, Deliver Type, Priority and Items</strong> — Save → label prints automatically.</>
          ) : (
            <>Scan an <strong>Order #</strong> to fetch the patient details from the latest roster, save the order, and print the label — all from one barcode gun. (Roster upload is handled by your dispatcher.)</>
          )}
        </p>
      </Card>

      {/* Roster upload + status — hidden from pharmacy_staff (no upload access). */}
      {canUpload && (
      <Card className="p-4" data-testid="roster-card">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <FileSpreadsheet className="h-3 w-3" /> Roster (xlsx or csv)
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              onChange={onFileChange}
              className="mt-1 block text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-primary file:text-primary-foreground file:font-medium hover:file:bg-primary/90"
              data-testid="roster-file-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Required columns: <code className="bg-secondary px-1 rounded">order_number</code>, <code className="bg-secondary px-1 rounded">patient_name</code>, <code className="bg-secondary px-1 rounded">phone</code>, <code className="bg-secondary px-1 rounded">address</code>. Optional: <code className="bg-secondary px-1 rounded">delivery_notes</code>. Each upload is added to the right-side list — delete sheets individually.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">In roster</div>
            <div className="font-display text-2xl font-semibold leading-none" data-testid="roster-count">
              {uploading ? "…" : rosterStatus?.remaining ?? 0}
              <span className="text-xs text-muted-foreground font-sans font-normal ml-1">/ {rosterStatus?.total ?? 0}</span>
            </div>
            {!!rosterStatus?.total && (
              <button
                type="button"
                onClick={clearRoster}
                className="mt-1 text-[11px] text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
                data-testid="roster-clear-btn"
              >
                <Trash2 className="h-3 w-3" /> Clear roster
              </button>
            )}
          </div>
        </div>
      </Card>
      )}

      {/* Column-mapping dialog — shown only after a file is inspected */}
      {canUpload && inspectData && mapping && (
        <Card className="p-4 border-l-4 border-l-primary" data-testid="roster-mapping">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Map columns</div>
              <h3 className="font-display font-semibold text-base">Match your spreadsheet columns to our fields</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Pick one or more columns for each field. Multiple selections are joined with a space (e.g., First + Last name).
                {inspectData.saved_mapping && <span className="ml-1 text-primary">Pre-filled from your last upload.</span>}
              </p>
            </div>
            <button
              type="button"
              onClick={cancelMapping}
              className="text-muted-foreground hover:text-foreground p-1"
              data-testid="roster-mapping-cancel"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3">
            {[
              { key: "primary_key", label: "PRIMARY KEY (QR)", required: false, hint: "Optional — Magnum Compounding: column 1 QR that maps to Order # in column 2" },
              { key: "order_number", label: "Order #", required: true, hint: "Used as the scan key" },
              { key: "customer_name", label: "Patient name", required: false, hint: "Pick First + Last to auto-concat" },
              { key: "customer_phone", label: "Phone", required: false },
              { key: "address", label: "Address", required: false },
              { key: "delivery_notes", label: "Delivery notes", required: false },
            ].map((field) => (
              <MappingRow
                key={field.key}
                field={field}
                columns={inspectData.columns}
                samples={inspectData.samples}
                value={mapping[field.key] || []}
                onChange={(cols) => setMapping((m) => ({ ...m, [field.key]: cols }))}
              />
            ))}
          </div>

          {/* Per-batch defaults — applied to every order created from this upload */}
          <div className="mt-4 pt-3 border-t border-border" data-testid="batch-defaults">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Batch defaults — applied to every order from this sheet</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Pharmacy <span className="text-destructive">*</span></label>
                <Select value={defaultPharmacyId} onValueChange={setDefaultPharmacyId}>
                  <SelectTrigger data-testid="batch-default-pharmacy"><SelectValue placeholder="Pick a pharmacy" /></SelectTrigger>
                  <SelectContent>
                    {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.nickname ? `${p.nickname} — ${p.name}` : p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Deliver Type</label>
                <Select value={defaultDeliverType} onValueChange={setDefaultDeliverType}>
                  <SelectTrigger data-testid="batch-default-deliver-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same_day">Same Day</SelectItem>
                    <SelectItem value="next_day">Next Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Priority</label>
                <Select value={defaultPriority} onValueChange={setDefaultPriority}>
                  <SelectTrigger data-testid="batch-default-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-border">
            <Button
              variant="outline"
              onClick={cancelMapping}
              disabled={uploading}
              className="rounded-full"
              data-testid="roster-mapping-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmMappingAndUpload}
              disabled={uploading || !mapping.order_number || mapping.order_number.length === 0}
              className="rounded-full"
              data-testid="roster-mapping-confirm-btn"
            >
              {uploading ? "Uploading…" : "Upload roster with this mapping"}
            </Button>
          </div>
        </Card>
      )}

      {/* 3-scan phase indicator + live preview */}
      <Card className="p-3" data-testid="scan-phase-indicator">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider mb-2">
          <StepDot active={phase === "idle"} done={phase !== "idle"} label="1 · Pull info" testid="phase-1" />
          <span className="h-px flex-1 bg-border" />
          <StepDot active={phase === "loaded"} done={phase === "created" || phase === "done"} label="2 · Save" testid="phase-2" />
          <span className="h-px flex-1 bg-border" />
          <StepDot active={phase === "created"} done={phase === "done"} label="3 · Print" testid="phase-3" />
        </div>
        {phase === "idle" && (
          <p className="text-xs text-muted-foreground" data-testid="phase-help">
            Scan the customer barcode to <strong>pull patient + batch defaults</strong> from the roster.
          </p>
        )}
        {phase === "loaded" && pulled && (
          <div className="text-xs space-y-0.5" data-testid="phase-loaded-preview">
            <div className="text-accent font-semibold">Ready to save — scan #{pulled.order_number} again</div>
            <div><span className="text-muted-foreground">Patient:</span> <strong>{pulled.customer_name || "—"}</strong></div>
            <div><span className="text-muted-foreground">Phone:</span> {pulled.customer_phone || "—"}</div>
            <div><span className="text-muted-foreground">Address:</span> {pulled.address || "—"}</div>
            <div><span className="text-muted-foreground">Deliver:</span> {(pulled.default_deliver_type || "same_day").replace("_", " ")} · <span className="text-muted-foreground">Priority:</span> {pulled.default_priority || "normal"}</div>
          </div>
        )}
        {phase === "created" && createdOrder && (
          <div className="text-xs text-[#1E6FE0] font-semibold" data-testid="phase-created-preview">
            Order #{createdOrder.order_number || createdOrder.id?.slice(0, 8)} saved — scan one more time to print the 4×6 label
          </div>
        )}
        {phase === "done" && (
          <div className="text-xs text-accent font-semibold" data-testid="phase-done-preview">
            Done · resetting for the next package…
          </div>
        )}
        {phase !== "idle" && (
          <button
            type="button"
            onClick={resetFlow}
            className="mt-2 text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="scan-reset-btn"
          >
            Reset
          </button>
        )}
      </Card>

      {/* Scan input — auto-submits on Enter OR 650 ms idle */}
      <Card className="p-4">
        <form onSubmit={submit}>
          <label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Keyboard className="h-3 w-3" /> Scanner gun / customer barcode
          </label>
          <Input
            ref={inputRef}
            value={code}
            onChange={(e) => onInputChange(e.target.value)}
            placeholder="Scan customer code — auto-submits on ENTER or after 0.65 s"
            className="mt-1"
            autoFocus
            disabled={busy}
            data-testid="customer-scan-input"
          />
          <p className="text-[11px] text-muted-foreground mt-2">
            Tip: most USB scanner guns send ENTER at the end of every scan, which submits automatically. No clicking needed.
          </p>
        </form>
      </Card>

      {duplicate && (
        <Card className="p-4 border-l-4 border-l-destructive bg-destructive/5" data-testid="customer-scan-duplicate">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-destructive uppercase tracking-wide text-sm">
                {duplicate.message || "Duplicate scan"}
              </div>
              {duplicate.order && (
                <div className="text-sm mt-2 space-y-0.5">
                  <div>
                    <span className="text-muted-foreground text-xs uppercase tracking-wider mr-1">Existing order:</span>
                    <span className="font-mono">#{duplicate.order.order_number || duplicate.order.id?.slice(0, 8)}</span>
                  </div>
                  {duplicate.order.customer_name && (
                    <div className="text-xs text-muted-foreground">
                      Patient: <span className="text-foreground">{duplicate.order.customer_name}</span>
                    </div>
                  )}
                  {duplicate.order.status && (
                    <div className="text-xs text-muted-foreground">
                      Status: <span className="text-foreground capitalize">{duplicate.order.status.replace("_", " ")}</span>
                    </div>
                  )}
                  {duplicate.order.created_at && (
                    <div className="text-[11px] text-muted-foreground">
                      Created {new Date(duplicate.order.created_at).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                Scan was ignored. To inspect this package, switch to the <strong>Inventory Scan</strong> tab.
              </p>
            </div>
          </div>
        </Card>
      )}

      {lookupError && !duplicate && (
        <Card className="p-4 border-l-4 border-l-amber-500" data-testid="customer-scan-not-in-roster">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Not in current roster</div>
              <div className="text-sm text-muted-foreground">{lookupError}</div>
              <Button onClick={goManual} variant="outline" size="sm" className="mt-3 rounded-full" data-testid="customer-scan-manual-btn">
                <PackagePlus className="h-3.5 w-3.5 mr-1.5" /> Create order manually
              </Button>
            </div>
          </div>
        </Card>
      )}
      </div>

      {/* Right-side sheet list — hidden from pharmacy_staff (read-only role) */}
      {canUpload && (
      <Card className="p-3 lg:sticky lg:top-4" data-testid="roster-sheets-panel">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Uploaded sheets</div>
            <div className="font-display font-semibold text-sm mt-0.5">{sheets.length} file{sheets.length === 1 ? "" : "s"}</div>
          </div>
        </div>
        {sheets.length === 0 ? (
          <p className="text-xs text-muted-foreground italic px-1 py-3" data-testid="sheets-empty">No sheets uploaded yet.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="sheets-list">
            {sheets.map((s) => (
              <li key={s.id} className="py-2 flex items-start gap-2 text-xs" data-testid={`sheet-row-${s.id}`}>
                <FileSpreadsheet className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate" title={s.filename}>{s.filename}</div>
                  <div className="text-muted-foreground text-[11px]">
                    {s.uploaded_at ? new Date(s.uploaded_at).toLocaleString() : "—"}
                  </div>
                  <div className="text-[11px] mt-0.5">
                    <span className="text-primary font-mono">{s.rows_remaining}</span>
                    <span className="text-muted-foreground"> of {s.rows_remaining + s.rows_consumed} remaining</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => deleteSheet(s)}
                  className="text-muted-foreground hover:text-destructive p-1 rounded transition-colors shrink-0"
                  title="Delete sheet"
                  data-testid={`sheet-delete-${s.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      )}
    </div>
  );
}

// Mapping row — one target field with a chip-style multi-column picker.
function StepDot({ active, done, label, testid }) {
  const cls = done
    ? "bg-accent text-accent-foreground border-accent"
    : active
    ? "bg-primary text-primary-foreground border-primary"
    : "bg-background text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${cls}`} data-testid={testid}>
      <span className="font-mono text-[10px]">{label}</span>
    </span>
  );
}

function MappingRow({ field, columns, samples, value, onChange }) {
  const toggleCol = (c) => {
    if (value.includes(c)) onChange(value.filter((x) => x !== c));
    else onChange([...value, c]);
  };
  const preview = samples && samples[0]
    ? value.map((c) => (samples[0][c] || "").toString().trim()).filter(Boolean).join(" ")
    : "";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-2 items-start" data-testid={`mapping-row-${field.key}`}>
      <div className="pt-1">
        <div className="text-sm font-medium">
          {field.label}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </div>
        {field.hint && <div className="text-[10px] text-muted-foreground leading-snug">{field.hint}</div>}
      </div>
      <div>
        <div className="flex flex-wrap gap-1.5">
          {columns.map((c) => {
            const active = value.includes(c);
            const order = active ? value.indexOf(c) + 1 : null;
            return (
              <button
                key={c}
                type="button"
                onClick={() => toggleCol(c)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:border-primary/50"}`}
                data-testid={`mapping-col-${field.key}-${c}`}
              >
                {active && <span className="mr-1 font-mono text-[10px] opacity-80">#{order}</span>}
                {c}
              </button>
            );
          })}
          {value.length === 0 && (
            <span className="text-xs text-muted-foreground italic px-2 py-1">{field.required ? "Pick at least one column" : "(optional)"}</span>
          )}
        </div>
        {preview && (
          <div className="text-[11px] text-muted-foreground mt-1">
            Sample → <span className="text-foreground font-mono">{preview}</span>
          </div>
        )}
      </div>
    </div>
  );
}
